import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { deleteDoc, doc, getDoc } from "firebase/firestore";

// Firebase
import { projectFirestore } from "../../firebase/config";

// Hooks
import { useAuthContext } from "../../hooks/useAuthContext";

// Components
import Delete from "../../components/Delete";
import Error from "../../components/Error";
import Loading from "../../components/Loading";

// Styles
import "../../styles/pages/ProjectDetails.css";

// ProjectDetails component to display project details
function ProjectDetails() {
  // Get project ID from URL params
  const { id } = useParams();

  // Use navigate for redirection
  const navigate = useNavigate();

  // Auth context to get current user
  const { user } = useAuthContext();

  // State for project details and loading/error states
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // State for delete modal and delete operation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // State to store project creator and tagged user details
  const [usersMap, setUsersMap] = useState({});

  // Fetch project details from Firestore when component mounts
  useEffect(() => {
    const fetchProject = async () => {
      try {
        // Reset loading and error state before fetching
        setLoading(true);
        setError(null);

        // Get project document reference
        const projectRef = doc(projectFirestore, "projects", id);

        // Fetch project document
        const projectSnapshot = await getDoc(projectRef);

        // Check if project exists
        if (!projectSnapshot.exists()) {
          setError("Project not found.");
          return;
        }

        // Combine Firestore document ID with project data
        const projectData = {
          id: projectSnapshot.id,
          ...projectSnapshot.data(),
        };

        // Store project data
        setProject(projectData);

        // Fetch creator + tagged users
        const userIds = new Set();

        // Add project creator
        if (projectData.createdBy) {
          userIds.add(projectData.createdBy);
        }

        // Add tagged users
        projectData.taggedUsers?.forEach((uid) => {
          userIds.add(uid);
        });

        // Fetch user details in parallel and create a map of uid to user data
        const map = {};

        await Promise.all(
          [...userIds].map(async (uid) => {
            const userDoc = await getDoc(doc(projectFirestore, "users", uid));

            if (userDoc.exists()) {
              map[uid] = {
                id: userDoc.id,
                ...userDoc.data(),
              };
            }
          })
        );

        // Store fetched user details
        setUsersMap(map);
      } catch (err) {
        console.error("Error loading project:", err);

        setError("Failed to load project.");
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [id]);

  // Function to handle project deletion
  const handleDelete = async () => {
    try {
      // Show deleting state
      setDeleting(true);

      // Delete project from Firestore
      await deleteDoc(doc(projectFirestore, "projects", id));

      // Close delete modal
      setShowDeleteModal(false);

      // Clear any existing errors
      setError(null);

      // Redirect back to projects page
      navigate("/projects");
    } catch (err) {
      console.error("Error deleting project:", err);

      setError("Failed to delete project.");
      setDeleting(false);
    }
  };

  // Function to format Firestore timestamps
  const formatDate = (timestamp) => {
    if (!timestamp?.toDate) {
      return "—";
    }

    return timestamp.toDate().toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  // If loading, show loading component
  if (loading) {
    return <Loading message="Loading project" />;
  }

  // If an error occurred, show error component
  if (error) {
    return <Error message={error} />;
  }

  // Extra safeguard in case project data is missing
  if (!project) {
    return <Error message="Project not found." />;
  }

  // Check if user is owner or tagged user for edit/delete permissions
  const isOwner = user?.uid === project.createdBy; // Owner of project
  const isTaggedUser = project.taggedUsers?.includes(user?.uid); // Tagged users
  const canEdit = isOwner || isTaggedUser; // Allow edit access

  // Get project creator details
  const creator = usersMap[project.createdBy];

  // Get tagged contributors and prevent creator from appearing twice
  const contributors = project.taggedUsers
    ?.filter((uid) => uid !== project.createdBy)
    .map((uid) => ({
      uid,
      ...usersMap[uid],
    }))
    .filter((person) => person.name);

  // Normalise GitHub URL
  const githubUrl = project.github
    ? project.github.startsWith("http")
      ? project.github
      : `https://${project.github}`
    : null;

  return (
    <main className="project-details-page text-light">
      {/* Project Hero */}
      <section className="project-details-hero position-relative overflow-hidden">
        {/* Background Grid */}
        <div className="project-details-grid position-absolute top-0 start-0 w-100 h-100" />

        <div className="container position-relative">
          {/* Back Link */}
          <Link
            to="/projects"
            className="project-back-link d-inline-flex align-items-center gap-2 text-decoration-none mb-5"
          >
            <span>←</span>
            Back to projects
          </Link>

          <div className="row align-items-end g-5">
            <div className="col-lg-8">
              {/* Project Label */}
              <span className="section-label d-block mb-3">
                PROJECT / {project.id?.slice(0, 6).toUpperCase()}
              </span>

              {/* Name */}
              <h1 className="project-details-title fw-bold mb-4">{project.name}</h1>

              {/* Summary */}
              {project.summary && (
                <p className="project-details-summary text-secondary mb-0">{project.summary}</p>
              )}
            </div>

            {/* Project Metadata */}
            <div className="col-lg-4">
              <div className="project-details-meta border-top border-secondary-subtle">
                {/* Created Date */}
                <div className="py-3 border-bottom border-secondary-subtle">
                  <small className="project-meta-label d-block font-monospace text-secondary mb-1">
                    CREATED
                  </small>

                  <span>{formatDate(project.createdAt)}</span>
                </div>

                {/* Last Modified Date */}
                <div className="py-3 border-bottom border-secondary-subtle">
                  <small className="project-meta-label d-block font-monospace text-secondary mb-1">
                    LAST UPDATED
                  </small>

                  <span>{formatDate(project.lastModified)}</span>
                </div>

                {/* Creator */}
                {creator && (
                  <div className="py-3">
                    <small className="project-meta-label d-block font-monospace text-secondary mb-2">
                      CREATED BY
                    </small>

                    <Link
                      to={`/profile/${encodeURIComponent(creator.name)}`}
                      className="project-creator-link d-inline-flex align-items-center gap-2 text-light text-decoration-none"
                    >
                      {creator.photoURL ? (
                        <img
                          src={creator.photoURL}
                          alt={creator.name}
                          className="project-small-avatar rounded-circle"
                        />
                      ) : (
                        <span className="project-small-avatar project-avatar-fallback rounded-circle d-inline-flex align-items-center justify-content-center fw-bold">
                          {creator.name?.charAt(0).toUpperCase() || "?"}
                        </span>
                      )}

                      <span>{creator.name}</span>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project Content */}
      <section className="project-details-content">
        <div className="container">
          <div className="row g-5">
            {/* Main Project Description */}
            <div className="col-lg-8">
              <span className="section-label d-block mb-3">01 / THE PROJECT</span>

              {/* Description */}
              <div className="project-description">
                <div
                  dangerouslySetInnerHTML={{
                    __html: project.description,
                  }}
                />
              </div>
            </div>

            {/* Project Sidebar */}
            <div className="col-lg-4">
              <aside className="project-sidebar">
                {/* Project Tags */}
                {project.tags?.length > 0 && (
                  <div className="project-sidebar-section">
                    <small className="project-meta-label d-block font-monospace text-secondary mb-3">
                      TECHNOLOGIES
                    </small>

                    <div className="d-flex flex-wrap gap-2">
                      {project.tags.map((tag) => (
                        <span key={tag} className="project-tag rounded-pill px-3 py-2">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Project Members */}
                {(creator || contributors?.length > 0) && (
                  <div className="project-sidebar-section">
                    <small className="project-meta-label d-block font-monospace text-secondary mb-3">
                      CONTRIBUTORS
                    </small>

                    <div className="d-flex flex-column gap-3">
                      {/* Creator */}
                      {creator && <ProjectMember person={creator} role="Creator" />}

                      {/* Tagged Users */}
                      {contributors?.map((person) => (
                        <ProjectMember key={person.uid} person={person} role="Contributor" />
                      ))}
                    </div>
                  </div>
                )}

                {/* Github Link */}
                {githubUrl && (
                  <div className="project-sidebar-section">
                    <small className="project-meta-label d-block font-monospace text-secondary mb-3">
                      LINKS
                    </small>

                    <a
                      href={githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-outline-light w-100 d-flex align-items-center justify-content-between"
                    >
                      <span>
                        <i className="bi bi-github me-2" />
                        View on GitHub
                      </span>

                      <span>↗</span>
                    </a>
                  </div>
                )}

                {/* Action Buttons */}
                {canEdit && (
                  <div className="project-sidebar-section">
                    <small className="project-meta-label d-block font-monospace text-secondary mb-3">
                      MANAGE PROJECT
                    </small>

                    <div className="d-grid gap-2">
                      {/* Edit Project */}
                      <Link
                        to={`/projects/${id}/edit`}
                        className="btn btn-outline-light d-flex align-items-center justify-content-between"
                      >
                        <span>
                          <i className="bi bi-pencil me-2" />
                          Edit Project
                        </span>

                        <span>→</span>
                      </Link>

                      {/* Only allow delete if user is owner */}
                      {isOwner && (
                        <button
                          type="button"
                          className="btn btn-outline-danger d-flex align-items-center justify-content-between"
                          onClick={() => setShowDeleteModal(true)}
                        >
                          <span>
                            <i className="bi bi-trash me-2" />
                            Delete Project
                          </span>

                          <span>×</span>
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </aside>
            </div>
          </div>
        </div>
      </section>

      {/* More Projects CTA */}
      <section className="project-details-footer border-top border-secondary-subtle">
        <div className="container">
          <div className="row align-items-center g-4">
            <div className="col-lg-8">
              <small className="project-meta-label font-monospace text-secondary d-block mb-2">
                MORE FROM BENOMA
              </small>

              <h2 className="fw-bold mb-0">Explore what else we&apos;ve been building.</h2>
            </div>

            <div className="col-lg-4 text-lg-end">
              <Link
                to="/projects"
                className="btn btn-danger btn-lg d-inline-flex align-items-center gap-2"
              >
                All Projects
                <span>→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Delete Modal */}
      {showDeleteModal && (
        <Delete
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteModal(false)}
          busy={deleting}
        />
      )}
    </main>
  );
}

// Reusable project member row
function ProjectMember({ person, role }) {
  return (
    <Link
      to={`/profile/${encodeURIComponent(person.name)}`}
      className="project-member d-flex align-items-center gap-3 text-decoration-none"
    >
      {/* Profile Image */}
      {person.photoURL ? (
        <img
          src={person.photoURL}
          alt={person.name}
          className="project-member-avatar rounded-circle"
        />
      ) : (
        <span className="project-member-avatar project-avatar-fallback rounded-circle d-inline-flex align-items-center justify-content-center fw-bold">
          {person.name?.charAt(0).toUpperCase() || "?"}
        </span>
      )}

      {/* Member Details */}
      <div className="min-w-0">
        <span className="text-light d-block text-truncate">{person.name}</span>

        <small className="text-secondary">{role}</small>
      </div>
    </Link>
  );
}

export default ProjectDetails;
